"use client";

import { ArrowDownUp, ArrowUpDown, BarChart2, ChartArea, ChevronLeft, ChevronRight, CodeXmlIcon, Eye, Folder, MousePointerClick, TrendingUp, UsersIcon, VideoIcon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import DateFilter from "../components/dashboard/DateRangeFilter";

/* ================= Collapse Icon ================= */
const CollapseIcon = ({ isExpanded, onClick }) => (
  <button
    onClick={onClick}
    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 bg-gray-200 transition-all duration-200 cursor-pointer group"
  >
    <svg
      className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 group-hover:text-gray-700 ${isExpanded ? "rotate-180" : ""
        }`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  </button>
);

/* ================= Metric Bar ================= */
const MetricBar = ({ value, max = 150, color = "blue" }) => {
  const percentage = Math.min((value / max) * 100, 100);

  const colorClasses = {
    blue: "from-blue-500 to-blue-600",
    green: "from-green-500 to-green-600",
    purple: "from-purple-500 to-purple-600",
    orange: "from-orange-500 to-orange-600",
  };

  return (
    <div className="flex items-center gap-3 w-full">
      <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${colorClasses[color]} rounded-full transition-all duration-700 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-sm font-semibold text-gray-700 min-w-[40px] text-right">
        {value}
      </span>
    </div>
  );
};

/* ================= Stat Card ================= */
const StatCard = ({ title, value, trend, color, icon }) => {
  const bgColor = color.replace("text-", "bg-").replace("-600", "-50");

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${bgColor}`}>
          <span className="text-2xl">{icon}</span>
        </div>
        {trend !== undefined && (
          <span
            className={`text-sm px-3 py-1 rounded-full font-medium ${trend > 0 ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
              }`}
          >
            {trend > 0 ? "↗" : "↘"} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-gray-600 text-sm font-medium mb-2">{title}</p>
      <p className={`text-3xl font-bold ${color}`}>
        {value.toLocaleString()}
      </p>
    </div>
  );
};

/* ================= Date Filter ================= */
const DateFilterComponent = ({ filter, onFilterChange }) => (
  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-8">
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Site
          </label>
          <select value={filter.site} onChange={(e) => onFilterChange({ ...filter, site: e.target.value })} className="w-full text-gray-900 px-4 py-3 border border-gray-200 rounded-xl">
            <option value="">All Sites</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date
          </label>
          <DateFilter className="w-full" value={filter.startDate} onDateChange={(date) => onFilterChange({ ...filter, startDate: date })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date
          </label>

          <DateFilter className="w-full"
            value={filter.endDate}
            onDateChange={(date) => onFilterChange({ ...filter, endDate: date })} />
        </div>

      </div>
      <div className="flex items-end gap-3">
        <button
          onClick={() => onFilterChange({ startDate: "", endDate: "" })}
          className="px-6 py-3 text-sm font-medium border border-gray-300 rounded-xl"
        >
          Clear
        </button>
      </div>
    </div>
  </div>
);

/* ================= Expanded Details ================= */
const ExpandedDetails = ({ data }) => (
  <div className="bg-gradient-to-br from-gray-50 to-blue-50 border border-gray-200 rounded-2xl p-6 mt-3 mx-4 mb-4">
    <div className="flex items-center justify-between mb-6">
      <h4 className="font-semibold text-gray-800 text-md flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <span className="text-gray-600"><ChartArea className="h-5 w-5" /></span>
        </div>
        Performance Details for {data.site}
      </h4>
      <div className="text-sm text-gray-500 font-bold">
        {new Date(data.dateTime).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
      </div>
    </div>

    <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
      {/* Progress Metrics */}
      <div className="space-y-6">
        <h5 className="font-semibold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
          <span>
            <BarChart2 className="h-5 w-5" />
          </span>
          Video Progress
        </h5>
        <div className="space-y-4">
          {[
            { label: "Start", value: data.start, color: "blue" },
            { label: "First Quartile", value: data.firstQuartile, color: "blue" },
            { label: "Midpoint", value: data.midpoint, color: "purple" },
            { label: "Third Quartile", value: data.thirdQuartile, color: "purple" },
            { label: "Complete", value: data.complete, color: "green" }
          ].map((metric, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100">
              <span className="text-sm font-medium text-gray-600 min-w-[120px]">{metric.label}</span>
              <div className="flex-1 max-w-[200px]">
                <MetricBar value={metric.value} color={metric.color} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="space-y-6">
        <h5 className="font-semibold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
          <span>
            <UsersIcon className="h-5 w-5" />
          </span>
          User Engagement
        </h5>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: "Mutes", value: data.mute, bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-100" },
            { label: "Unmutes", value: data.unmute, bg: "bg-green-50", text: "text-green-700", border: "border-green-100" },
            { label: "Pauses", value: data.pause, bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-100" },
            { label: "Resumes", value: data.resume, bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-100" },
          ].map((metric, index) => (
            <div key={index} className={`p-4 rounded-xl border-2 ${metric.bg} ${metric.border} text-center transition-all duration-300 hover:scale-105`}>
              <div className={`text-2xl font-bold ${metric.text} mb-1`}>{metric.value}</div>
              <div className={`text-xs font-medium ${metric.text} opacity-80`}>{metric.label}</div>
            </div>
          ))}
        </div>
        <div className={`p-4 rounded-xl border-2 bg-red-50 border-red-100 text-center transition-all duration-300 hover:scale-105`}>
          <div className="text-2xl font-bold text-red-700 mb-1">{data.clickthrough}</div>
          <div className="text-xs font-medium text-red-700 opacity-80">Click-throughs</div>
        </div>
      </div>

      {/* Resources */}
      <div className="space-y-6">
        <h5 className="font-semibold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
          <span>
            <Folder className="h-5 w-5" />
          </span>
          Resources
        </h5>
        <div className="space-y-4">
          <a
            href={data.creativeUrl}
            target="_blank"
            rel="noreferrer"
            className="block p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-300 group"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <span className="text-xl">
                  <VideoIcon className="h-5 w-5" />
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 text-sm mb-1">Creative Asset</div>
                <div className="text-xs text-gray-500 truncate">{data.creativeUrl}</div>
              </div>
              <div className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                ↗
              </div>
            </div>
          </a>
          <a
            href={data.xmlUrl}
            target="_blank"
            rel="noreferrer"
            className="block p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-green-300 hover:shadow-md transition-all duration-300 group"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <span className="text-xl">
                  <CodeXmlIcon className="h-5 w-5" />
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 text-sm mb-1">VAST XML</div>
                <div className="text-xs text-gray-500 truncate">{data.xmlUrl}</div>
              </div>
              <div className="text-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                ↗
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>
  </div>
);

/* ================= Main Dashboard ================= */
export default function DashboardPage() {
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
  });

  const [expandedRows, setExpandedRows] = useState({});

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    total: 0
  });

  const [sorting, setSorting] = useState({
    sortBy: "dateTime",
    order: "desc"
  });

  useEffect(() => {
    // fetchReports();
  }, [pagination.page, pagination.limit, sorting, dateFilter]);

  const fetchReports = async () => {
    try {
      setLoading(true);

      const response = await api.get("/reports", {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          sortBy: sorting.sortBy,
          order: sorting.order,
          startDate: dateFilter.startDate,
          endDate: dateFilter.endDate,
        },
      });

      setReports(response.data.data);
      setPagination((prev) => ({
        ...prev,
        total: response.data.total,
      }));
    } catch (error) {
      console.error("Error fetching reports", error);
    } finally {
      setLoading(false);
    }
  };
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const data = useMemo(
    () => [
      {
        site: "example.com",
        start: 100,
        firstQuartile: 115,
        midpoint: 110,
        thirdQuartile: 102,
        complete: 96,
        mute: 300000,
        unmute: 18,
        pause: 22,
        resume: 20,
        clickthrough: 15,
        dateTime: "2026-02-19T10:30:00Z",
        creativeUrl: "https://test-pages.b-cdn.net/MTN_16-9.mp4",
        xmlUrl: "https://example.com/vast.xml",
        xmlCount: 200
      },
      {
        site: "example.com",
        start: 130,
        firstQuartile: 125,
        midpoint: 120,
        thirdQuartile: 112,
        complete: 96,
        mute: 30,
        unmute: 18,
        pause: 22,
        resume: 20,
        clickthrough: 15,
        dateTime: "2025-10-14T10:30:00Z",
        creativeUrl: "https://test-pages.b-cdn.net/MTN_16-9.mp4",
        xmlUrl: "https://example.com/vast.xml",
        xmlCount: 200
      },
      {
        site: "news.site",
        start: 80,
        firstQuartile: 75,
        midpoint: 70,
        thirdQuartile: 65,
        complete: 60,
        mute: 10,
        unmute: 8,
        pause: 12,
        resume: 10,
        clickthrough: 7,
        dateTime: "2025-10-15T14:45:00Z",
        creativeUrl: "https://cdn.example.com/ad.mp4",
        xmlUrl: "https://cdn.example.com/vast.xml",
        xmlCount: 500
      },
      {
        site: "blog.example.com",
        start: 150,
        firstQuartile: 142,
        midpoint: 135,
        thirdQuartile: 128,
        complete: 120,
        mute: 45,
        unmute: 25,
        pause: 30,
        resume: 28,
        clickthrough: 22,
        dateTime: "2025-10-14T09:15:00Z",
        creativeUrl: "https://cdn.example.com/video2.mp4",
        xmlUrl: "https://cdn.example.com/vast2.xml",
        xmlCount: 250
      },
      {
        site: "shop.demo",
        start: 95,
        firstQuartile: 90,
        midpoint: 85,
        thirdQuartile: 78,
        complete: 72,
        mute: 22,
        unmute: 15,
        pause: 18,
        resume: 16,
        clickthrough: 12,
        dateTime: "2025-10-13T16:20:00Z",
        creativeUrl: "https://cdn.example.com/promo.mp4",
        xmlUrl: "https://cdn.example.com/vast3.xml",
        xmlCount: 380
      }
    ],
    []
  );

  const filteredData = useMemo(() => {
    if (!dateFilter.startDate && !dateFilter.endDate) return data;

    return data.filter((item) => {
      const itemDate = new Date(item.dateTime);
      const start = dateFilter.startDate
        ? new Date(dateFilter.startDate)
        : null;
      const end = dateFilter.endDate ? new Date(dateFilter.endDate) : null;

      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;
      return true;
    });
  }, [data, dateFilter]);

  const summary = useMemo(() => {
    const totalStarts = filteredData.reduce((sum, row) => sum + row.start, 0);
    const totalCompletions = filteredData.reduce((sum, row) => sum + row.complete, 0);
    const totalClicks = filteredData.reduce((sum, row) => sum + row.clickthrough, 0);
    const totalXmlCount = filteredData.reduce((sum, row) => sum + row.xmlCount, 0);
    const completionRate = totalStarts > 0 ? (totalCompletions / totalStarts * 100) : 0;

    return {
      totalStarts,
      totalCompletions,
      totalClicks,
      totalXmlCount,
      completionRate
    };
  }, [filteredData]);

  const handleSort = (column) => {
    setSorting((prev) => ({
      sortBy: column,
      order:
        prev.sortBy === column && prev.order === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getRowId = (item) =>
    `${item.dateTime}-${item.site}-${item.xmlUrl}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
      {/* Header */}
      <div className="mb-2">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
          <div className="mb-1 lg:mb-0">
            <h1 className="text-4xl font-bold text-gray-900 mb-0">Ad Performance Dashboard</h1>
          </div>
        </div>

        {/* Date Filter */}
        <DateFilterComponent
          filter={dateFilter}
          onFilterChange={setDateFilter}
        />

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total XML Hits"
            value={summary.totalXmlCount}
            trend={15}
            color="text-blue-600"
            icon={<CodeXmlIcon className="w-6 h-6 text-blue-600" />}
          />
          <StatCard
            title="Total Impressions"
            value={summary.totalStarts}
            trend={12}
            color="text-green-600"
            icon={<Eye className="w-6 h-6 text-blue-600" />}
          />
          <StatCard
            title="Click-throughs"
            value={summary.totalClicks}
            trend={-3}
            color="text-purple-600"
            icon={<MousePointerClick className="w-6 h-6 text-purple-600" />}
          />
          <StatCard
            title="Completion Rate"
            value={Math.round(summary.completionRate)}
            trend={5}
            color="text-orange-600"
            icon={<TrendingUp className="w-6 h-6 text-orange-600" />}
          />
        </div>
      </div>

      {/* Main Data Table */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Tracking Report</h2>
              <p className="text-gray-600 mt-2">Click on any row to view detailed performance metrics</p>
            </div>
            <div className="mt-4 lg:mt-0 flex items-center gap-4">
              <span className="px-4 py-2 bg-blue-100 text-black rounded-lg text-sm font-medium">
                {filteredData.length} records
              </span>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 border-b border-gray-200">
              <tr>
                <th className="px-6 py-5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">
                  {/* Collapse icon column */}
                </th>
                <th onClick={() => handleSort("dateTime")}
                  className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer">
                  <div className="flex gap-1">
                    Date & Time
                    {sorting.sortBy === "dateTime" && (
                      <span>{sorting.order === "asc" ? <ArrowDownUp className="h-4 w-4" /> : <ArrowUpDown className="h-4 w-4" />}</span>
                    )}
                  </div>
                </th>
                <th onClick={() => handleSort("site")}
                  className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer">
                  <div className="flex gap-1">
                    Advertiser
                    {sorting.sortBy === "site" && (
                      <span>{sorting.order === "asc" ? <ArrowDownUp className="h-4 w-4" /> : <ArrowUpDown className="h-4 w-4" />}</span>
                    )}
                  </div>
                </th>
                <th onClick={() => handleSort("creativeName")}
                  className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer">
                  <div className="flex gap-1">
                    Creative Name
                    {sorting.sortBy === "creativeName" && (
                      <span>{sorting.order === "asc" ? <ArrowDownUp className="h-4 w-4" /> : <ArrowUpDown className="h-4 w-4" />}</span>
                    )}
                  </div>
                </th>
                <th onClick={() => handleSort("xmlUrl")}
                  className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer">
                  <div className="flex gap-1"> XML URL
                    {sorting.sortBy === "xmlUrl" && (
                      <span>{sorting.order === "asc" ? <ArrowDownUp className="h-4 w-4" /> : <ArrowUpDown className="h-4 w-4" />}</span>
                    )}
                  </div>
                </th>
                <th onClick={() => handleSort("hitCount")}
                  className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer">
                  <div className="flex gap-1">
                    Hit Count
                    {sorting.sortBy === "hitCount" && (
                      <span>{sorting.order === "asc" ? <ArrowDownUp className="h-4 w-4" /> : <ArrowUpDown className="h-4 w-4" />}</span>
                    )}
                  </div>
                </th>
                <th onClick={() => handleSort("impressions")}
                  className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer">
                  <div className="flex gap-1">
                    Impressions
                    {sorting.sortBy === "impressions" && (
                      <span>{sorting.order === "asc" ? <ArrowDownUp className="h-4 w-4" /> : <ArrowUpDown className="h-4 w-4" />}</span>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/60">
              {filteredData.map((item) => {
                const rowId = getRowId(item);
                const isExpanded = expandedRows[rowId];

                return (
                  <React.Fragment key={rowId}>
                    <tr
                      key={rowId}
                      className={`hover:bg-gray-50/80  transition-all duration-200 ${isExpanded ? 'bg-blue-50/50' : ''
                        }`}
                    >
                      {/* Collapse Icon */}
                      <td className="px-6 py-5 ">
                        <CollapseIcon
                          isExpanded={isExpanded}
                          onClick={() => toggleRow(rowId)}
                        />
                      </td>

                      {/* Date */}
                      <td className="px-6 py-5">
                        <div className="text-gray-900 font-semibold">
                          {new Date(item.dateTime).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </td>

                      {/* Site */}
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded-full ${item.site === "example.com" ? "bg-gradient-to-r from-green-400 to-green-500" :
                            item.site === "news.site" ? "bg-gradient-to-r from-blue-400 to-blue-500" :
                              item.site === "blog.example.com" ? "bg-gradient-to-r from-purple-400 to-purple-500" :
                                item.site === "shop.demo" ? "bg-gradient-to-r from-orange-400 to-orange-500" : "bg-gray-400"
                            }`}></div>
                          <span className="font-semibold text-gray-900">{item.site}</span>
                        </div>
                      </td>

                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-gray-900">{item.advertiser || '-'}</span>
                        </div>
                      </td>

                      {/* XML URL */}
                      <td className="px-6 py-5">
                        <a
                          href={item.xmlUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-700 hover:underline font-medium text-sm truncate max-w-[240px] block transition-all duration-200"
                          title={item.xmlUrl}
                        >
                          {item.xmlUrl.replace('https://', '')}
                        </a>
                      </td>

                      {/* Hit Count */}
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-start">
                          <span className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-bold text-sm shadow-sm">
                            {item.xmlCount.toLocaleString()}
                          </span>
                        </div>
                      </td>

                      {/* Impression Count */}
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-start">
                          <span className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white font-bold text-sm shadow-sm">
                            {item.start.toLocaleString()}
                          </span>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Details Row */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} className="p-0">
                          <ExpandedDetails data={item} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Table Footer */}
        <div className="px-6 py-6 border-t border-gray-200 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-4">
              <span>Showing {filteredData.length} of {data.length} records</span>
              {dateFilter.startDate && (
                <span className="px-3 py-1 bg-white rounded-lg border border-gray-200">
                  Filtered: {dateFilter.startDate} to {dateFilter.endDate || 'present'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button className="flex items-center px-4 py-2 rounded-lg hover:bg-white transition-all duration-200 font-medium"
                disabled={pagination.page === 1}
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    page: prev.page - 1,
                  }))
                }>
                <ChevronLeft size={18} /> Previous
              </button>
              <span className="px-4 py-2 bg-white rounded-lg border border-gray-200 font-medium">
                Page {pagination.page} of {totalPages}
              </span>
              <button className="flex items-center px-4 py-2 rounded-lg hover:bg-white transition-all duration-200 font-medium"
                disabled={pagination.page === totalPages}
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }))
                }>
                Next  <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          Data updates in real-time • Last updated: {new Date().toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
}
